import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Message } from '../../classes/Message';
import { includes, lowerCase, uniqBy } from 'lodash';
import { JobsService, UserService } from '../../services';
import { EventService } from '../../services';
import { Subscription } from 'rxjs';
import { RefreshJobEventTypeEnum } from '../../enums/refresh-job-event-type.enum';
import moment from 'moment';
import { MessageService } from 'primeng';
import { OrganisationTypeEnum } from '../../enums/organisation-types';

@Component({
  selector: 'job-screen-history-timeline',
  styleUrls: ['job-screen-history-timeline.component.scss'],
  templateUrl: 'job-screen-history-timeline.component.html',
  encapsulation: ViewEncapsulation.None, // todo: check why
})
export class JobScreenHistoryTimelineComponent implements OnInit, OnDestroy {
  public isLoading: boolean = false;

  private _jobId: number;
  @Input('jobId')
  set jobId(value: number) {
    this._jobId = value;
  }

  get jobId(): number {
    return this._jobId;
  }

  private _organisationTypeId: number;
  @Input('organisationTypeId')
  set organisationTypeId(value: number) {
    this._organisationTypeId = value;
  }

  get organisationTypeId(): number {
    return this._organisationTypeId;
  }

  newNoteIsCustomer: boolean = false;
  newNoteIsSupplier: boolean = false;
  newNoteIsEngineius: boolean = false;

  filterEngineius: boolean = true;
  filterCustomer: boolean = true;
  filterSupplier: boolean = true;

  historys: any[];
  fullHistorys: any[];
  newHistoryNote: string;

  filterHistorySearch: string = '';

  selectedEventType: any;
  eventTypes: any[] = [];

  selectedEventTask: any;
  eventTasks: any[] = [];

  refreshEventSub: Subscription;

  constructor(
    private jobService: JobsService,
    private messageService: MessageService,
    private eventService: EventService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    this.loadHistorys();
    this.refreshEventSub = this.eventService.RefreshJobEvent.subscribe(
      (eventType: RefreshJobEventTypeEnum) => {
        if (eventType == RefreshJobEventTypeEnum.RefreshHistory) {
          this.loadHistorys();
        }
      },
    );
  }

  // Users with the CUSTOMER_BOOKING role should not be able to see pricing, so the job history should be filtered to exclude pricing changes.
  async excludePricingHistorysIfApplicable() {
    const hasCustomerBookingRole: boolean = await this.userService.isUserInRole('CUSTOMER_BOOKING');
    // hide history for the customer price role too
    // as there is no easy way to show max price or price range conditionally
    const hasCustomerPriceRole: boolean = await this.userService.isUserInRole('CUSTOMER_PRICE');

    if (hasCustomerBookingRole || hasCustomerPriceRole) {
      this.historys = this.historys.filter((x) => x.area != 'CustomerPriceChange');
    }
  }

  ngOnDestroy() {
    if (this.refreshEventSub) {
      this.refreshEventSub.unsubscribe();
    }
  }

  loadHistorys() {
    this.isLoading = true;
    this.historys = [];
    this.fullHistorys = [];
    this.eventTypes = [];
    this.eventTasks = [];
    this.filterHistorySearch = '';

    this.jobService
      .getHistory(this._jobId)
      .then(async (result) => {
        this.isLoading = false;
        this.fullHistorys = result;
        this.historys = result;

        await this.excludePricingHistorysIfApplicable();

        this.historys.forEach((x) => {
          x.eventDateTime = this.getEventTime(x);
        });

        this.eventTypes.push({ label: 'Select event type', value: null });

        let classes = uniqBy(this.historys, 'area');
        classes.forEach((x) => {
          this.eventTypes.push({ label: x.area, value: x.area });
        });

        this.eventTasks.push({ label: 'Select task', value: null });
        let tasks = uniqBy(this.historys, 'taskName');
        tasks.forEach((x) => {
          if (x.taskName) this.eventTasks.push({ label: x.taskName, value: x.taskName });
        });
      })
      .catch((ex) => {
        this.isLoading = false;
        this.messageService.add(
          Message.Error('An error occurred while loading Job History.', 'Load History'),
        );
      });
  }

  getEventTime(history) {
    return moment(history.created).fromNow();
  }

  getRegPlate(plate: string) {
    if (plate.length == 7) {
      return plate.substr(0, 4) + ' ' + plate.substr(4);
    } else {
      return plate;
    }
  }

  filterEventTypeChanged() {
    this.filterHistorys();
  }

  filterHistorys() {
    let tags: string[] = ['Engineius', 'Customer', 'Supplier'];
    if (!this.filterEngineius) tags = tags.filter((x) => x != 'Engineius');
    if (!this.filterCustomer) tags = tags.filter((x) => x != 'Customer');
    if (!this.filterSupplier) tags = tags.filter((x) => x != 'Supplier');

    //console.log("Filter event type: ", this.selectedEventType);

    const lowerCaseTags = tags.map((x) => lowerCase(x));

    this.historys = this.fullHistorys.filter((x) => includes(lowerCaseTags, x.tagType));
    this.historys = this.historys.filter(
      (x) => x.area == this.selectedEventType || !this.selectedEventType,
    );
    this.historys = this.historys.filter(
      (x) => x.taskName == this.selectedEventTask || !this.selectedEventTask || !x.taskName,
    );

    if (this.filterHistorySearch.trim().length > 0) {
      const searchTerms = this.filterHistorySearch.trim().split(/\s+/);

      this.historys = this.historys.filter((x) => {
        return searchTerms.every((term) => {
          const escapedTerm = term.replace(/([.+?^${}()|\[\]\/\\])/g, '\\$1');
          const regexPattern = new RegExp(escapedTerm.replace(/\*/g, '.*'), 'i');

          return regexPattern.test(x.description) || regexPattern.test(x.userFullName);
        });
      });
    }
  }

  addNewNote() {
    var viewIsEngineius = false;
    var viewIsCustomer = false;
    var viewIsSupplier = false;
    if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
      viewIsEngineius = this.newNoteIsEngineius;
      viewIsCustomer = this.newNoteIsCustomer;
      viewIsSupplier = this.newNoteIsSupplier;
    } else if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
      viewIsCustomer = true;
    } else if (this.organisationTypeId == OrganisationTypeEnum.Supplier) {
      viewIsSupplier = true;
    }
    var d = new Date();

    this.jobService
      .addHistory({
        jobId: this.jobId,
        note: this.newHistoryNote,
        viewIsEngineius: viewIsEngineius,
        viewIsCustomer: viewIsCustomer,
        viewIsSupplier: viewIsSupplier,
        createdOn: d,
      })
      .then((result) => {
        this.newHistoryNote = '';
        this.newNoteIsEngineius = false;
        this.newNoteIsCustomer = false;
        this.newNoteIsSupplier = false;

        this.loadHistorys();
      })
      .catch((ex) => {
        this.messageService.add(
          Message.Error(
            'An error occurred while updating the data. The current data may not be correct.',
            'Add New Note',
          ),
        );
      });
  }
}
